import { useEffect, useId, useState } from "react";

import { Modal } from "../Modal";
import { Button } from "./Button";
import { Input } from "./Input";

/**
 * Diálogo que pide un dato de texto (design system §6.8) — reemplazo de
 * `window.prompt`, que no se puede estilar, no valida y no es accesible.
 *
 * Como `ConfirmDialog`, es controlado: el caller maneja `open` y recibe el
 * valor ya recortado en `onConfirm`. El valor se reinicia cada vez que se
 * abre. Enter envía; el botón de acción usa verbo+objeto.
 */
interface PromptDialogProps {
  open: boolean;
  title: string;
  /** Contexto: qué se va a tocar y qué consecuencia tiene. */
  description?: React.ReactNode;
  /** Label del campo (siempre visible, no sólo placeholder). */
  label: string;
  placeholder?: string;
  hint?: string;
  /** Largo mínimo del valor recortado. Default 1 (obligatorio). */
  minLength?: number;
  maxLength?: number;
  /** Texto del botón de acción (verbo + objeto). */
  confirmLabel: string;
  cancelLabel?: string;
  /** `danger` para acciones destructivas (rose). Default `default`. */
  tone?: "danger" | "default";
  loading?: boolean;
  onConfirm: (valor: string) => void;
  onCancel: () => void;
}

export function PromptDialog({
  open,
  title,
  description,
  label,
  placeholder,
  hint,
  minLength = 1,
  maxLength,
  confirmLabel,
  cancelLabel = "Cancelar",
  tone = "default",
  loading = false,
  onConfirm,
  onCancel,
}: PromptDialogProps): JSX.Element {
  const formId = useId();
  const [valor, setValor] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValor("");
      setError(null);
    }
  }, [open]);

  const enviar = (e: React.FormEvent): void => {
    e.preventDefault();
    const limpio = valor.trim();
    if (limpio.length < minLength) {
      setError(
        minLength <= 1
          ? `Falta: ${label.toLowerCase()}`
          : `Escribe al menos ${minLength} caracteres`,
      );
      return;
    }
    onConfirm(limpio);
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      closeOnBackdrop={!loading}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant={tone === "danger" ? "destructive" : "primary"}
            disabled={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={enviar} className="space-y-3" noValidate>
        {description && (
          <div className="text-sm text-neutral-600">{description}</div>
        )}
        <Input
          label={label}
          value={valor}
          onChange={(e) => {
            setValor(e.target.value);
            if (error) setError(null);
          }}
          placeholder={placeholder}
          hint={hint}
          error={error ?? undefined}
          maxLength={maxLength}
          required
        />
      </form>
    </Modal>
  );
}
